'use strict';
import {ICurationItem} from '../../interfaces/curation-item.interface';
import {IRevision} from '@ispm/core/interfaces/workflow.interface';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {
  WorkflowBarActionsCreatorService
} from '@ispm/shared/components/workflow-bar/services/workflow-bar-actions-creator.service';
import {DynamicModalLoaderService} from '@ispm/layout/services/dynamic-modal-loader.service';
import {CurationActionCreatorService} from '../../services/curation-action-creator.service';
import {ConfirmModalComponent} from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import {modalResponse} from '@ispm/layout/constants/modal-responses.enum';
import {ICurationUpdateRequest} from '../../interfaces/curation-update-request.interface';
import {CurationUtils} from '../../utils/curation-utils';
import {PlaceholderModalComponent} from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import {ModalTypes} from '@ispm/layout/components/modals/contants/modal-types.enum';
import {get, has, isEqual, set, unset, isEmpty, trim} from 'lodash';
import {
  RelatedEntitiesRevision
} from '@ispm-client/modules/widgets/components/related-entities/interfaces/related-entities-revision-interface';
import {relatedEntitiesRevisionTable} from '../../classes/relatedEntitiesRevisionTable';
import {CurationSettings} from '../curation-dashboard/curation-setting';
import {CurationEditorHelper} from './curation-editor-helper';
import {PdfService} from '@ispm/dashboard/test/services/pdf.service';
import {PanelLoaderPayload} from '@ispm/layout/classes/panel-loader-payload.abstract';
import {NotesModalWrapperComponent} from '../../../notes/components/notes-modal-wrapper/notes-modal-wrapper.component';
import {PanelsService} from '@ispm/layout/services/panels.service';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {DISCARD_WARNING, DRAG, DRAGS_LIST, ITEM_TITLE, LIST_TITLE} from '@ispm/shared/constants/curation_constants';
import {
  CurationEditorActionMessageComponent
} from './curation-editor-action-message/curation-editor-action-message.component';
import {TranslateService} from '@ngx-translate/core';
import {map, retry, take} from 'rxjs/operators';
import {CurationItemPermissions} from './curation-enums';
import {FormHandlerService} from '@ispm/dynamic-form/services/form-handler.service';
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {
  ReassignTestModalComponent
} from '@ispm/layout/components/modals/reassign-test-modal/reassign-test-modal.component';
import {DiagnosticOrderService} from '@ispm/dashboard/test/services/diagnostic-order.service';
import {ActivityService} from '@ispm/user/services/activity.service';
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {throwError} from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {NotesService} from '@ispm/user/services/notes.service';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {HttpInterceptorService} from "@ispm/core/services/http-interceptor.service";
import {MenuItem} from "primeng/api";
import {ActivityActions} from "@ispm/user/constants/activityActions";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Component({
  template: ''
})
export abstract class CurationEditorAbstract implements OnInit, OnDestroy {
  constructor(
    protected authService: AuthenticationService,
    protected formHandler: FormHandlerService,
    public notificationMessageService: NotificationMessageService,
    public workflowBarActionsCreatorService: WorkflowBarActionsCreatorService,
    protected modalLoader: DynamicModalLoaderService,
    public activatedRoute: ActivatedRoute,
    public panelService: PanelsService,
    public notesService: NotesService,
    public coreAC: CoreActionCreatorService,
    public curationActionCreatorService: CurationActionCreatorService,
    private translateService: TranslateService,
    protected diagnosticOrderService: DiagnosticOrderService,
    public userActionCreatorService: UserActionCreatorService,
    public activityService: ActivityService,
    private _http: HttpInterceptorService,
    private router: Router,
    private pdfService?: PdfService,
    public detector?: ChangeDetectorRef,
    public workflowConfigService?: WorkflowConfigService,
    public routerManagerService?: RouterManagerService
  ) {
  }

  get actorId() {
    return this.authService.user.info.id;
  }

  get content() {
    return this.curationData ? this.curationData['content'] : undefined;
  }

  get drugs() {
    return this.content ? this.content['drugs'] : undefined;
  }

  set drugs(_drugs) {
    this.curationItem.curationData['content']['drugs'] = _drugs;
  }

  get therapies() {
    return this.content ? this.content['therapies'] : undefined;
  }

  set therapies(therapies) {
    this.curationItem.curationData['content']['therapies'] = therapies;
  }

  get rationalization() {
    return this.content ? this.content['rationalization'] : undefined;
  }

  get readyForRun(): string {
    return this.content ? this.content['readyForRun'] : undefined;
  }

  get curationData() {
    return this.curationItem ? this.curationItem.curationData : undefined;
  }

  set revisionCount(revCount: number) {
    if (revCount > 0) {
      this._revisionCount = revCount;
      this.setRevisionTag();
    }
  }

  get revisionCount(): number {
    return this._revisionCount;
  }

  get updatedFieldsContext() {
    return this.approvedMode ? null : this.revisionTag;
  }

  get disabledField() {
    return this.curationStatus === CurationSettings.CURATION_STATUS_DRAFT ? '' : '{title}';
  }

  public readonly NOTE_ACTION_APPROVED = 'approved';
  public readonly NOTE_ACTION_REJECTED = 'rejected';

  public curationItem: ICurationItem;
  public oldCurationItem: ICurationItem;
  public curationStatus: string;
  public curationKey: string;
  public revisionsData: Array<{ revision: IRevision, item: { [prop: string]: any } }>;
  public revisionLastUpdatedDate: { [prop: string]: string } = {};
  private revisionLastOwner: { [prop: string]: string } = {};
  public isDataLoaded: boolean = false;
  public oldRevisionIsShown: boolean = false;
  public oldRevisionMessage: string;
  public approvalUserMode: boolean = false;
  public readOnlyUserMode: boolean = false;
  public publishUserMode: boolean = false;
  public isReviewer: boolean = false;
  public curationType: string;
  public curationId: string;
  public readOnlyMode: boolean = false;
  public actionsDisabledMode: boolean = false;
  public curatorUserMode: boolean = false;
  public inlineEdit: boolean = false; // Means that components are in edit mode (no need to click on the 'pencil')
  public editorFormsMap: any;
  public validationFormsMap: Map<string, boolean> = new Map<string, boolean>();
  public isValid: boolean = true;
  public currentRevisionName: string;
  public currentRevisionId: string;
  public submitted: boolean = false;
  public approvedMode: boolean = false;
  public revisionTag: string; // RevisionTag is in use by dynamic forms to detect user changes
  public _revisionCount: number = 0;
  public revisedRelatedEntities = []; // holds details of ALL related entities of the current curation that were revised
  public revisiedEntitiesTableData: relatedEntitiesRevisionTable = new relatedEntitiesRevisionTable();
  public revisedEntitiesRevisionsData: RelatedEntitiesRevision[];
  public conf: any;
  public automaticNotes: boolean;
  public formData: any = {};
  public loading: boolean = false;
  public confMap: any;
  public isCurationItemAssignedToCurrentUser: boolean = false;
  public showPreviewButton: boolean = false;
  public sameOrganization: boolean = false;
  public showStopWorkflowButton: boolean = false;
  public newNotes: number = 0;
  public notesPanelClass: string = '';
  public showArchiveReason: boolean = false;
  public lastManualOpinionName: string;

  protected modalSubscriber$: Subscription;
  protected destroyer$: Subject<null> = new Subject();
  protected relevantProperty: Object;
  protected listDataPath: string = 'drugs';

  private verifyRelatedEntitiesBeforeArchiveTypes: boolean;
  public multiStepReview: boolean;
  private assignedPractitionerId: string;
  private discardRevision: boolean = false;
  private differentOrganization: boolean = false;
  public isReassignButtonDisabled = false;
  private showArchiveReasonConf: any;
  private currentNotesPanelTitle: string;
  private isNotesPanelOpen: boolean = false;
  private notesPanelId = 'CURATION-NOTES-PANEL';
  private noteActorType = 'Practitioner';

  abstract setInitParams();

  abstract postGetCurationItem();

  abstract getCurationKey();

  abstract postUpdateCurationItem();

  public postReassignCurationItem() {
  }

  public postArchiveRejectionCurationItem() {
  }

  public postRejectionCurationItem() {
  }

  onFailure() {
  }

  setRevisionTag() {
    if (this.curationItem) {
      if (this.curationItem['curationItem']['id'] && this._revisionCount > 0) {
        this.revisionTag = this.curationItem['curationItem']['id'] + '__' + this._revisionCount.toString();
      }
    }
  }

  ngOnInit() {
    this.curationType = this.routerManagerService.parameters?.curationType;
    this.curationActionCreatorService.getAssignedPractioners().pipe(take(1)).subscribe(practIds => {
      this.assignedPractitionerId = (!isEmpty(practIds)) ? practIds[0] : null;
    });
    this.curationActionCreatorService.getMultiStepReviewFlag().pipe(take(1)).subscribe(status => {
      this.multiStepReview = 'true' === status;
    }); // default OFF

    const curationId = this.routerManagerService.parameters?.curationId;
    this.currentRevisionId = this.routerManagerService.parameters?.revisionId;
    this.getDataAndConfiguration(curationId);
  }

  protected getOldRevisionMessage(revisionId: string,
                                  revisions: Array<{ revision: IRevision, item: { [prop: string]: any } }>): string {
    const index = revisions.findIndex(element => revisionId === element.revision.id);
    if (index === revisions.length - 1) {
      return '';
    }
    const latestRevision = revisions[revisions.length - 1];
    if (latestRevision && latestRevision.revision && CurationUtils.isTempStatus(latestRevision.revision.activeJobName)) {
      return 'A later revision is being processed';
    }
    return 'A later version/draft of this item exists';
  }


  protected shouldDeployAsFinal(status: string): boolean {
    // Deployment as final should occur when sending for review and when publishing:
    // - Reviewer should only get to review a validated and finalized pathway.
    // - In case of publishing without a review first, the pathway must be validated and finalized.
    return this.isInReviewStatus(status) || status === CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED;
  }


  isInReviewStatus(status: string): boolean {
    return status === CurationSettings.CURATION_STATUS_WAITING_FOR_APPROVAL || status === CurationSettings.CURATION_STATUS_INREVIEW;
  }

  getDataAndConfiguration(curationId, afterReassign: boolean = false) {
    this.curationActionCreatorService.getCurationItemV1(curationId, this.currentRevisionId, this.curationType).subscribe(res => {
        if (res.status === CurationEditorHelper.ERROR_ITEM_NOT_FOUND.toString()) {
          this.notificationMessageService.error('Cannot load curation item: ' + res['error'] + '. ' + res['details']);
        } else {
          this.curationItem = res;
          this.curationKey = this.getCurationKey();
          this.oldCurationItem = JSON.parse(JSON.stringify(res));
          this.curationStatus = this.curationItem.opinion['to']; // Status is saved in 'to' field of opinion
          this.curationId = this.curationItem['curationItem']['id'];
          this.currentRevisionId = res['curationData']['revisionId'];
          this.curationKey = this.getCurationKey();
          this.oldRevisionIsShown = this.currentRevisionId !== this.curationItem['curationItem']['activeRevisionId'];
          this.approvedMode = this.isPublishedStatus(this.curationStatus);
          const workflowRequestId: string = this.curationItem['curationItem']['workflowRequestId'];
          this.checkUnreadNotes();
          this.setUserModes();

          Observable.forkJoin(this.curationActionCreatorService.getState(['definitions']).take(1),
            this.curationActionCreatorService.getCurationRevisions(workflowRequestId))
            .subscribe(async ([definitions, revisions]) => {
                const defs: any = definitions;
                this.conf = Array.from(defs.values()).filter(data =>
                  data['type'] === this.curationType)[0];

                const generalConf = Array.from(defs.values()).filter(data =>
                  data['type'] === CurationSettings.CURATION_KB)[0];

                this.readOnlyUserMode = this.readOnlyUserMode || get(this.conf, 'ui.readOnly', false);

                // editor forms map can be Array with dependency of data value or simple object with list of forms
                const relevantPropertyDescr =
                  this.getPropertyEditorMapsDescriptor(get(this.conf, 'ui.forms.editorForms', undefined),
                    this.curationItem.curationData['content']);
                if (!!relevantPropertyDescr) {
                  this.relevantProperty = {
                    'property': relevantPropertyDescr['property'],
                    'value': relevantPropertyDescr['value']
                  };
                  this.editorFormsMap = relevantPropertyDescr;
                } else {
                  this.relevantProperty = {};
                  this.editorFormsMap = get(this.conf, 'ui.forms.editorForms', undefined);
                }
                this.automaticNotes = this.conf['automaticNotes'] || false;
                this.setListDataPath();
                this.setInitParams();
                const type: string = this.curationType;
                this.verifyRelatedEntitiesBeforeArchiveTypes = generalConf['ui']['verifyRelatedEntitiesBeforeArchiveTypes'] ?
                  generalConf['ui']['verifyRelatedEntitiesBeforeArchiveTypes'].indexOf(type) > -1 : false;

                this.curationActionCreatorService.getRevisionOpinions(revisions, '_sort:desc', 'timestamp')
                  .subscribe(opinions => {
                    this.revisionLastOwner = this.getLastOwner(opinions);
                    this.propagateLastOwner(revisions, this.revisionLastOwner);
                    this.lastManualOpinionName = this.revisionLastOwner[this.currentRevisionId];
                    this.revisionLastUpdatedDate = this.getLastUpdatedDate(opinions);
                    this.detector.markForCheck();
                  });

                this.setupRevisionData(revisions);
                this.calculateState(afterReassign);
                this.formData = this.curationItem.curationData['content'];
                this.checkRevisedRelatedEntities();
                await this.getAllRelatedEntitiesRevisions().then(() => this.postGetCurationItem());
                this.showPreviewButton = this.defineShowPreviewButton(this.conf['runPreview']);
                this.differentOrganization = this.curationData['authorization'].find(item => item.resourceType === 'Organization').reference !== this.authService.user['organization'];
                this.sameOrganization = !this.differentOrganization;
                this.showStopWorkflowButton = get(this.conf, 'showStopWorkflowButton', false);

                this.showArchiveReasonConf = get(generalConf, 'ui.archiveReason', null);
                if (!!this.showArchiveReasonConf) {
                  this.showArchiveReason = get(this.showArchiveReasonConf, 'showArchiveReasonPopup', false);
                }

                if (afterReassign)
                  this.postReassignCurationItem();

                this.detector.detectChanges();
              }
            );
        }
      }
    );
  }

  private getLastOwner(opinions: Array<any>) {
    const lastOwner: { [prop: string]: string } = {};
    if (!isEmpty(opinions)) {
      opinions.forEach(op => {
        const workflow = op.context.find(itm => itm.resourceType.toLocaleLowerCase() === 'workflowrevision');
        const revisionId = workflow.reference;
        if (op.name && !lastOwner[revisionId] &&
            (op.to == CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED
            || op.to == CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED
            || op.to == CurationSettings.CURATION_STATUS_ARCHIVED_SUBMITTED))
        {
            lastOwner[revisionId] = op.name;
        }
      });
    }
    return lastOwner;
  }

  private propagateLastOwner(revisions: Array<{ revision: IRevision; item: { [p: string]: any } }>, revisionLastOwner: {
    [p: string]: string
  }) {
    let lastOwner = undefined;
    for (let i = 0; i < revisions.length; i++) {
      const revId = revisions[i].revision.id;
      if (revisionLastOwner[revId]) {
        lastOwner = revisionLastOwner[revId];
      } else {
        revisionLastOwner[revId] = lastOwner;
      }
    }
  }

  private getLastUpdatedDate(opinions: Array<any>) {
    const lastUpdatedDates: { [prop: string]: string } = {};
    if (!isEmpty(opinions)) {
      opinions.forEach(op => {
        const workflow = op.context.find(itm => itm.resourceType.toLocaleLowerCase() === 'workflowrevision');
        const revisionId = workflow.reference;
        if (!lastUpdatedDates[revisionId] || (lastUpdatedDates[revisionId] < op.meta.lastUpdated)) {
          lastUpdatedDates[revisionId] = op.meta.lastUpdated;
        }
      });
    }
    return lastUpdatedDates;
  }

  protected setupRevisionData(revisions: Array<{ revision: IRevision; item: { [p: string]: any } }>) {
    this.revisionsData = revisions;
    this.revisionCount = this.revisionsData.length;
    this.oldRevisionMessage = this.oldRevisionIsShown ?
      this.getOldRevisionMessage(this.currentRevisionId, revisions) : '';
    this.currentRevisionName = this.getCurrentRevisionName(this.revisionsData);
  }

  protected currentUserId(): string {
    return this.authService.user.id;
  }

  protected setUserModes() {
    this.isCurationItemAssignedToCurrentUser = (!this.multiStepReview)
      || (this.assignedPractitionerId != null
        ? this.currentUserId() === this.assignedPractitionerId
        : this.currentUserId() === this.curationItem.opinion.practitioner);
    this.publishUserMode = this.isCurationItemAssignedToCurrentUser
      && (this.authService.doesUserHasPermission(CurationItemPermissions.PUBLISH_CURATION_ITEM_PERMISSION)
        || this.authService.doesUserHasPermission(this.getTypeSpecificPermission('PUBLISH')));
    this.curatorUserMode =
      this.authService.doesUserHasPermission(CurationItemPermissions.WRITE_CURATION_ITEM_PERMISSION)
      || this.authService.doesUserHasPermission(this.getTypeSpecificPermission('WRITE'));
    this.isReviewer = this.multiStepReview
      && (this.authService.doesUserHasPermission(CurationItemPermissions.REVIEW_CURATION_ITEM_PERMISSION)
        || this.authService.doesUserHasPermission(this.getTypeSpecificPermission('REVIEW')));
    this.approvalUserMode =
      (!this.multiStepReview && this.authService.doesUserHasPermission(CurationItemPermissions.APPROVE_CURATION_ITEM_PERMISSION))
      || (this.isReviewer && this.isCurationItemAssignedToCurrentUser);
    this.readOnlyUserMode = (!this.curatorUserMode) && (!this.approvalUserMode);
  }

  protected async isItemReadyForPublishing(): Promise<{ ready: boolean, msg?: string }> {
    let result: Promise<{ ready: boolean, msg?: string }> = Promise.resolve({ready: true});

    if (!this.multiStepReview) {
      return result;
    }

    const minReviewers: number = get(this.conf, 'ui.publish.minReviewers', 0);
    const lastReviewMustBeApproved: boolean = get(this.conf, 'ui.publish.lastReviewMustBeApproved', false);

    await this.getCurationItemNotes().toPromise().then(
      (res) => {
        if (res && res.status === 200 && has(res, 'body')) {
          const reviewNotes = get(res, 'body', []).filter(note => note.action);
          const isRejected: boolean = (!isEmpty(reviewNotes)) && reviewNotes[0].action === this.NOTE_ACTION_REJECTED;
          const reviews = reviewNotes.map(note => get(note, 'actor.id')).filter(actor => !!actor);
          const reviewers = new Set(reviews).size;
          const minReviewersFail = (reviewers < minReviewers);
          if (minReviewersFail || (lastReviewMustBeApproved && isRejected)) {
            const msg = minReviewersFail
              ? ('Only ' + reviewers + ' (out of ' + minReviewers + ' required) reviewers reviewed the item.')
              : 'The last review must be approved to enable publishing, but was rejected.';
            result = Promise.resolve({ready: false, msg});
          } else {
            result = Promise.resolve({ready: true});
          }
        } else {
          console.log('[curation-editor-absract.isItemReadyForPublishing] error reading curation item notes: ');
          result = Promise.resolve({ready: false, msg: 'Internal error: Failed to check reviews of curation item'});
        }
      }
    );
    return result;
  }

  private getCurationItemNotes() {
    return this.notesService.getWithContextType(this.currentRevisionId, 'WorkflowRequest', this.actorId, true);
  }

  private getTypeSpecificPermission(permission: string) {
    return `CURATIONITEM.${this.curationType.toUpperCase()}.${permission.toUpperCase()}`;
  }

  private getEditorFormsMap(editorFormsDescriptor: any, data: any): Object {
    return Array.isArray(editorFormsDescriptor) ? this.getByDescriptor(editorFormsDescriptor, data) : this.conf['ui']['forms']['editorForms'];
  }

  private getByDescriptor(descriptors: any, data: any): Object {
    const foundObj = descriptors.find(descr => {
      if (has(descr, 'property') && has(data, descr['property'])) {
        const propertyValue = get(data, descr['property'], undefined);
        const descrValue = get(descr, 'value', undefined);
        return !!propertyValue && !!descrValue && isEqual(propertyValue, descrValue);
      }
      return false;
    });
    return !!foundObj ? foundObj['forms'] : undefined;
  }

  private getPropertyEditorMapsDescriptor(descriptors: any, data: any): Object {
    if (Array.isArray(descriptors)) {
      const foundObj = descriptors.find(descr => {
        if (has(descr, 'property') && has(data, descr['property'])) {
          const propertyValue = get(data, descr['property'], undefined);
          const descrValue = get(descr, 'value', undefined);
          return !!propertyValue && !!descrValue && isEqual(propertyValue, descrValue);
        }
        return false;
      });
      return !!foundObj ? foundObj : undefined;
    }
    return undefined;
  }

  logActionMessage(curationData: any, revisionId: string, status: string, prevStatus?: string) {
    if (curationData) {
      const action = status === prevStatus ? 'Save' : 'Change status';
      const logMessage = 'Action: ' + action +
        ', RevisionId: ' + revisionId +
        ', CurationDataId: ' + curationData.id +
        ', by: ' + curationData.performerName +
        ', of type:' + curationData.content.type;
      if (curationData.relatedEntities) {
        console.log(logMessage + ', relatedEntities: ', JSON.parse(JSON.stringify(curationData.relatedEntities)));
      } else {
        console.log(logMessage);
      }
    }
  }

  updateCurationItem(status: string, data: ICurationUpdateRequest, reviewFeedback?: string, callback?: any, prevStatus?: string) {
    this.setLoadingState(true);
    if (status === undefined) {
      status = this.curationStatus;
    }
    this.logActionMessage(data.curationData, this.curationItem['curationItem']['activeRevisionId'], status, prevStatus);
    const curationItemId = this.curationItem['curationItem']['id'];
    const curationType = get(data, 'curationData.content.type', '').toUpperCase();
    this.curationActionCreatorService.updateCurationItemV1(curationItemId, data, this.curationItem['curationItem']['type'], data['status']).subscribe(res => {
      if (res['status'] && res['status'] === CurationEditorHelper.ERROR_NOT_LATEST_REVISION) {
        this.notificationMessageService.error('Cannot save changes: Curation was already updated');
        this.setLoadingState(false);
      } else if (res['error']) {
        const translationKey = 'CURATION_UPDATE_' + curationType + '_' + get(res, 'error.status', undefined);
        let errMsg: string = this.translateService.instant(translationKey);
        if (errMsg === translationKey) {
          errMsg = res['error']['error'] ? res['error']['error'] : res['error'];
        }
        this.notificationMessageService.error('Cannot save changes: ' + errMsg);
        console.error('updateCurationItem Error:', res['error']);
        this.setLoadingState(false);
        this.onFailure();
      } else if (res['status'] && res['status'] !== 200) {
        this.notificationMessageService.error('Failed to save changes');
        console.error('### updateCurationItemV1 error => ', res);
        this.setLoadingState(false);
      } else {
        // If the status did not change - show a message about successful save
        if (prevStatus === status) {
          this.notificationMessageService.success('Item saved successfully');
        }
        if (this.automaticNotes) {
          if (reviewFeedback === CurationSettings.CURATION_STATUS_APPROVED || reviewFeedback === CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED) {
            this.createNote(this.curationItem, this.NOTE_ACTION_APPROVED, this.curationItem['opinion']['approveMessage'] || '', '');
          } else if (reviewFeedback === CurationSettings.CURATION_STATUS_REJECTED) {
            this.createNote(this.curationItem, this.NOTE_ACTION_REJECTED, this.curationItem['opinion']['rejectMessage'] || '', '');
          }
        }
        if (status === CurationSettings.CURATION_STATUS_REVIEW_FEEDBACK_RECEIVED) {
          this.reassignCurationItemToOwner(res, status);
        }
        if (status !== CurationSettings.CURATION_STATUS_DRAFT
          && (status !== CurationSettings.CURATION_STATUS_REVIEW_FEEDBACK_RECEIVED
            || prevStatus === CurationSettings.CURATION_STATUS_INREVIEW)) {
          /* GEN-74176: If user clicked "publish", allow a few seconds for the item to switch to
           * "published" status (automatic switch from "publishedsubmitted"), otherwise a newly
           * created item might not be seen in the type-specific tab, which is strange for the user.
           */
          if (status === CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED) {
            this.waitUntilPublished(10, 500, curationItemId, this.currentRevisionId, this.curationType);
          } else {
            this.goBackToDashboard();
          }
        } else {
          const prevRevisionId = this.currentRevisionId;
          this.currentRevisionId = res['curationData']['revisionId'];
          if (prevRevisionId && this.currentRevisionId !== prevRevisionId) {
            // Each revise starts a new review cycle - make the current (revising) user the owner of the curation item
            if (this.isPublishedStatus(this.curationStatus) && status === CurationSettings.CURATION_STATUS_DRAFT) {
              const curationItem = this.curationItem['curationItem'];
              return this.curationActionCreatorService.setCurationItemOwner(curationItem.id, this.currentRevisionId,
                curationItem.workflowRequestId, this.currentUserId(), curationItem.type, status)
                .subscribe(() => {
                  this.setLoadingState(false);
                  const redirectUrl = 'curation/' + this.curationId + '/' + this.routerManagerService.parameters.workflowRequestId  + '/' + this.currentRevisionId + '/' + this.routerManagerService.parameters.curationType;
                  this.routerManagerService.reload(redirectUrl);
                });
            }
            this.setLoadingState(false);
            return;
          }
          this.curationActionCreatorService.getCurationRevisions(this.curationItem['curationItem']['workflowRequestId']).subscribe(
            (data => {
              this.setupRevisionData(data);
              // order of updated fields is important for angular trigers for depend components
              this.curationItem = res;
              this.postUpdateCurationItem();
              this.oldCurationItem = JSON.parse(JSON.stringify(res));
              this.curationStatus = status;
              this.curationKey = this.getCurationKey();
              this.calculateState();
              this.submitted = false; // in order to prevent fields highlight to be saved in case user didn't click on save
              this.checkRevisedRelatedEntities();
              this.getAllRelatedEntitiesRevisions()
                .then(() => {
                  this.setLoadingState(false);
                });
            })
          );
        }

        if (callback && typeof callback === 'function') {
          callback();
        }
      }
    });
  }

  waitUntilPublished(retries, queryPeriodMs, curationItemId, revisionId, type) {
    setTimeout(function () {
      if (retries === 0) {
        this.goBackToDashboard();
      } else {
        this.curationActionCreatorService.getCurationItemV1(curationItemId, revisionId, type, false).subscribe(res => {
          console.log(`getCurationItemV1(${curationItemId}, ${revisionId}, ${type}): ${res.opinion}`);
          if (res.curationItem && res.opinion && res.opinion['to'] === CurationSettings.CURATION_STATUS_PUBLISHED) {
            this.goBackToDashboard();
          } else {
            this.waitUntilPublished(retries - 1, queryPeriodMs, curationItemId, revisionId, type);
          }
        });
      }
    }.bind(this), queryPeriodMs);
  }

  goBackToDashboard() {
    this.setLoadingState(false);
    this.goBack();
  }

  discardCurationItem(status: string, data: ICurationUpdateRequest, callback?: any) {
    this.setLoadingState(true);
    if (status === undefined) {
      status = this.curationStatus;
    }
    this.curationActionCreatorService.discardCurrentRevision(this.curationItem['curationItem']['id'], data,
      this.curationItem['curationItem']['type'], data['status'])
      .subscribe(res => {
        if (res['error']) {
          const errMsg: string = res['error']['error'] ? res['error']['error'] : res['error'];
          this.notificationMessageService.error('Cannot discard current revision: ' + errMsg);
          console.error('discardCurrentRevision Error:', res['error']);
          this.setLoadingState(false);
        }
        else {
          this.router.navigate(['curation']);
        }
      });
  }

  private reassignCurationItemToOwner(res, status: string) {
    this.curationActionCreatorService.getOwnerId().subscribe(
      (ownerId) => {
        if (ownerId && this.curationItem) {
          const curationItem = this.curationItem['curationItem'];
          this.curationActionCreatorService.reassignCurationItem(curationItem['id'],
            res['curationData']['revisionId'], curationItem['workflowRequestId'], ownerId, curationItem['type'],
            this.getDbName(), status)
            .then(
              () => {
              },
              () => {
                const errMsg: string = 'Failed to reassign curation item back to owner';
                this.notificationMessageService.error(errMsg);
                console.error(errMsg);
              });
        }
      }
    );
  }

  updateCurationItemToPromise(status: string, data: ICurationUpdateRequest): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.curationActionCreatorService.updateCurationItemV1(this.curationItem['curationItem']['id'], data, this.curationItem['curationItem']['type'], data['status'])
        .subscribe(res => {
          if (res['status'] && res['status'] === CurationEditorHelper.ERROR_NOT_LATEST_REVISION) {
            this.notificationMessageService.error('Cannot save changes: Curation was already updated');
            this.setLoadingState(false);
            reject(false);
          } else if (res['error']) {
            const errMsg: string = res['error']['error'] ? res['error']['error'] : res['error'];
            this.notificationMessageService.error('Cannot save changes: ' + errMsg);
            console.error('updateCurationItem Error:', res['error']);
            this.setLoadingState(false);
            reject(false);
          } else {
            this.currentRevisionId = res['curationData']['revisionId'];
            this.curationActionCreatorService.getCurationRevisions(this.curationItem['curationItem']['workflowRequestId']).subscribe(
              (data => {
                this.setupRevisionData(data);
                // order of updated fields is important for angular trigers for depend components
                this.curationItem = res;
                this.postUpdateCurationItem();
                this.oldCurationItem = JSON.parse(JSON.stringify(res));
                this.curationStatus = status;
                this.curationKey = this.getCurationKey();
                this.calculateState();
                this.submitted = false; // in order to prevent fields highlight to be saved in case user didn't click on save
                this.checkRevisedRelatedEntities();
                this.getAllRelatedEntitiesRevisions()
                  .then(() => {
                    this.setLoadingState(false);
                  });
              })
            );
            resolve(true);

          }
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
    this.curationItem = null;
  }

  public removeDeletedItems(itemsRootNames: string[], status: string, updateInBackend: boolean = true) {
    // Permanently delete items marked as "Deleted = true". In use, for example, to remove deleted drugs before approving the curation item
    if (!itemsRootNames) {
      return Promise.resolve(true);
    }

    let dataChanged: boolean = false;

    for (const itemRootName of itemsRootNames) { // Getting root names - curation item can have more than one root elements that need to be clear (e.g. drugs and citation in TP)
      if (this.curationItem.curationData['content'][itemRootName]) {
        if (this.curationItem.curationData['content'][itemRootName].findIndex(item => item['deleted'] === true) > -1) {
          dataChanged = true;
          this.curationItem.curationData['content'][itemRootName] = this.curationItem.curationData['content'][itemRootName].filter(item => item['deleted'] !== true);
        }
      }
    }

    if (!dataChanged || !updateInBackend) {
      return Promise.resolve(true);
    }

    // Saving updated data in server
    const data = this.buildCurationData(status, 'rejectMessage');
    return new Promise((resolve, reject) => {
      this.curationActionCreatorService.updateCurationItemV1(this.curationItem['curationItem']['id'], data, this.curationItem['curationItem']['type'], data['status']).subscribe(res => {
        if (res['status'] && res['status'] === CurationEditorHelper.ERROR_NOT_LATEST_REVISION) {
          this.notificationMessageService.error('Cannot save changes: Curation was already updated');
          reject();
        } else if (res['error']) {
          this.notificationMessageService.error('Cannot save changes: ' + res['error']);
          console.error('updateCurationItem Error: ', res['error']);
          reject();
        } else {
          resolve(true);
        }
      });
    });
  }

  public calculateState(afterReassign: boolean = false) {
    this.differentOrganization = (this.curationData['authorization'].find(item => item.resourceType === 'Organization').reference !== this.authService.user['organization']);
    this.actionsDisabledMode = this.oldRevisionIsShown || this.readOnlyUserMode || this.differentOrganization;
    this.readOnlyMode = this.oldRevisionIsShown || this.readOnlyUserMode || !(this.isEditableStatus(this.curationStatus));
    this.inlineEdit = this.revisionsData && this.revisionsData.length === 1 && !this.readOnlyMode; // allow update in place only if no revision was saved before
  }

  public getCurrentRevisionName(revisionsData: any) {
    let revisionName: string = '';

    revisionsData.forEach(data => {
      const rev = data['revision'];
      if (rev['id'] === this.currentRevisionId) {
        revisionName = rev['name'];
      }
    });

    return revisionName;
  }

  cancel() {
    this.router.navigate(['curation']);
  }

  protected unsavedChangesExist(userAction: boolean = false): Observable<boolean> {
    return of(
      this.curationItem &&
      this.oldCurationItem &&
      (JSON.stringify(this.curationItem) !== JSON.stringify(this.oldCurationItem)) &&
      userAction &&
      this.curationStatus !== CurationSettings.CURATION_STATUS_APPROVED
    );
  }

  goBack(userAction: boolean = false) {
    this.unsavedChangesExist(userAction).pipe(
      take(1)
    ).subscribe(
      (unsavedChangesExist => {
        if (unsavedChangesExist) {
          const cont: string = 'You have unsaved changes.\n\rIf you return to the worklist without saving your changes, all changes will be lost.';
          this.modalSubscriber$ = this.modalLoader.popupModal(ConfirmModalComponent,
            {
              title: 'Unsaved changes',
              content: cont,
              approveBtn: 'Continue editing',
              declineBtn: 'Discard changes'
            })
            .filter(response => response.payload)
            .map(response => response.payload)
            .subscribe((response: modalResponse) => {
              switch (response) {
                case modalResponse.decline:
                  this.router.navigate(['curation']);
                  break;
                case modalResponse.approve:
                default:
                  return false;
              }
              this.modalSubscriber$.unsubscribe();
                this.modalSubscriber$ = null;
            },
              () => {
              },
              () => {
                this.modalSubscriber$ = null;
              });
        } else {
          this.router.navigate(['curation']);
        }
      })
    );
  }

  updateWhiteListFields() {
    if (!this.isValid) {
      this.notificationMessageService.error('Please fill in all mandatory fields');
      return;
    }
    const rejectMessage = '';
    const data = this.buildCurationData(this.curationStatus, rejectMessage);

    this.curationActionCreatorService.updateCurationWhiteListItemV1(this.curationItem['curationItem']['id'], data, this.curationItem['curationItem']['type']).subscribe(res => {
      if (res['error']) {
        this.notificationMessageService.error('Cannot save changes: ' + res['error']);
        console.error('updateCurationItem Error: ' + res['error'] + ', details: ' + res['details']);
      } else {
        this.currentRevisionId = res['curationData']['revisionId'];
        this.curationActionCreatorService.getCurationRevisions(this.curationItem['curationItem']['workflowRequestId']).subscribe(
          (data => {
            this.setupRevisionData(data);
            // order of updated fields is important for angular trigers for depend components
            this.curationItem = res;
            this.postUpdateCurationItem();
            this.oldCurationItem = JSON.parse(JSON.stringify(res));
            this.curationKey = this.getCurationKey();
            this.workflowBarActionsCreatorService.setWorkflowBar(this.curationItem['curationItem']['workflowRequestId'], this.curationItem['curationData']['revisionId'], true);
          })
        );
      }
    });
  }

  protected buildCurationData = (status, rejectMessage): ICurationUpdateRequest => ({
    status: status, curationData: this.curationItem.curationData,
    opinion: CurationUtils.buildOpinion(this.authService.user, '', rejectMessage)
  })

  async changeStatus(status: string, callback?: any, bypassFormSubmittingValidation: boolean = false) {
    this.setLoadingState(true);

    const previousStatus = this.curationStatus;
    if (status === CurationSettings.CURATION_STATUS_DRAFT && this.isPublishedStatus(this.curationStatus)) {
      this.handleStatusChange(status, previousStatus, callback, bypassFormSubmittingValidation);
    } else {
      // show discard message and continue if need to discard
      if (status === CurationSettings.CURATION_VIRTUAL_STATUS_DISCARD) {
        this.showWarningPopup('Discard Revision', DISCARD_WARNING).subscribe((res) => {
          if (res === true) {
            this.discardRevision = true;
            this.handleDiscardCurrentRevision(status, previousStatus, callback);
          }
          this.setLoadingState(false);
        });
      } else {
        if (this.multiStepReview && status === CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED) {
          const canPublish = await this.isItemReadyForPublishing();
          if (!canPublish.ready) {
            this.coreAC.setErrorModal(true, 'Item cannot be published', canPublish.msg, 'OK');
            console.error(canPublish.msg);
            this.setLoadingState(false);
            return;
          }
        }
        // GEN-52692: Show approval justification popup
        if (this.multiStepReview && status === CurationSettings.CURATION_STATUS_APPROVED) {
          this.openApprovalJustificationPopup((model) => {
            if (!this.curationItem['opinion']) {
              this.curationItem['opinion'] = {};
            }
            this.curationItem['opinion']['approveMessage'] = model['message'];
          }, () => {
            this.handleStatusChange(status, previousStatus, callback, bypassFormSubmittingValidation);
          });
          this.setLoadingState(false);
          return; // For now, don't do anything else; wait for workflow to change
        } else {
          this.handleStatusChange(status, previousStatus, callback, bypassFormSubmittingValidation);
        }
      }
    }
  }

  protected setLoadingState(loadingInProgress: boolean) {
    this.loading = loadingInProgress;
    if (this.detector) {
      this.detector.markForCheck();
    }
  }

  private handleStatusChange(srcStatus: string, previousStatus, callback, bypassFormSubmittingValidation: boolean = false) {
    this.submitted = !bypassFormSubmittingValidation; // save differences in order to highlight them next time user edits the current curation item
    let status = !!srcStatus ? srcStatus : previousStatus;
    this.computeNewStatus(status, previousStatus).subscribe(newStatus => {
      if (!this.isValid && !bypassFormSubmittingValidation) {
        this.notificationMessageService.error('Please fill in all mandatory fields');
        this.setLoadingState(false);
        return;
      }

      // Prevent saving\approving when having a link to an archived related entity (GEN-49357)
      const relatedEntityRoot = this.curationItem ? this.curationItem['curationData']['relatedEntities'] : undefined;
      if (relatedEntityRoot && status !== CurationSettings.CURATION_STATUS_REJECTED) { // Allow rejecting
        const archivedRelatedEntities: Array<any> = relatedEntityRoot.filter(item => item.upToDate === false && item.latestStatus === 'archivedsubmitted'); // Getting a list of all archuved related entities
        if (archivedRelatedEntities.length > 0) {
          const currentRelatedEntitiesIds = this.getCurrentRelatedEntitiesIds(); // Getting list of id's of related entity that are currently in use
          // Making sure that currently there are no archived entities (handle the case that when editor was opened there were archived entities but user select non archived entities and wants to save)
          if (currentRelatedEntitiesIds) {
            // Compering the current in use related entities with the list of archived entities (no need to check it on the server because user is not able to select archied entities so only the list client got is relevant for checking)
            for (const currentRelatedEntitiesId of currentRelatedEntitiesIds) {
              for (const archivedRelatedEntity of archivedRelatedEntities) {
                const curationItemIdBeforeUpdate: string = archivedRelatedEntity['identifiers'].find(idn => {
                  if (idn.system === 'curationItemId') {
                    return (idn);
                  }
                }).value;

                if (currentRelatedEntitiesId.indexOf(curationItemIdBeforeUpdate) > -1) {
                  this.notificationMessageService.error('This curation item contains an archived ' + archivedRelatedEntity['reviseItemType'][0].toUpperCase() +
                    archivedRelatedEntity['reviseItemType'].slice(1));
                  this.setLoadingState(false);
                  return;
                }
              }
            }
          }
        }
      }

      if (!srcStatus) {
        status = this.curationStatus;
      }

      this.prepareDataToSave(status, previousStatus)
        .then(result => {
            if (!result) {
              this.setLoadingState(false);
              return;
            }

            let rejectMessage: string;

            if (status === CurationSettings.CURATION_STATUS_REJECTED) {
              rejectMessage = this.curationItem['opinion']['rejectMessage'];
            }

            // build update object
            const data = this.buildCurationData(newStatus, rejectMessage);
            switch (status) {
              case CurationSettings.CURATION_STATUS_APPROVED:
              case CurationSettings.CURATION_STATUS_PUBLISHED:
              case CurationSettings.CURATION_STATUS_REJECTED:
              case CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED:
              case CurationSettings.CURATION_STATUS_ARCHIVED_SUBMITTED:
                // If server gets update for status approved, cannot pass curation data
                delete data.curationData;
                break;
              case CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE:
                if (previousStatus !== CurationSettings.CURATION_STATUS_DRAFT) {
                  delete data.curationData;
                }
                break;
            }

            // Ensuring curation item is not in use as a related entity before archiving
            if ((status === CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE || status === CurationSettings.CURATION_STATUS_ARCHIVED_SUBMITTED)
              && this.verifyRelatedEntitiesBeforeArchiveTypes) {
              // Getting related entities list. The list contains only last approved and 'waitforarchive' curation items.  Older revisions are filtered out in web layer (for example, if regimen is linked to a certain TP that has three revisions, the list contains THREE related entities).
              this.curationActionCreatorService.getRelatedEntities(this.curationItem['curationItem']['id'], true)
                .subscribe(res => {
                  if (res.length > 0) {
                    this.curationItem = this.oldCurationItem; // undo all data changes made by archiving action (GEN-145795)
                    console.warn('Curation item is in use and cannot be archived. It is linked by the following: ', res);
                    this.notificationMessageService.warning('Curation item is in use by other curation items and cannot be archived');
                    this.setLoadingState(false);
                    return;
                  } else {
                    this.updateCurationItem(newStatus, data, status, callback, previousStatus);
                  }
                });
            } else {
              this.updateCurationItem(newStatus, data, status, callback, previousStatus);
            }

            if (status === CurationSettings.CURATION_STATUS_DRAFT && previousStatus === CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE) {
              this.curationStatus = status;
              this.calculateState(false);
              this.postArchiveRejectionCurationItem();
            } else if (status === CurationSettings.CURATION_STATUS_DRAFT && previousStatus === CurationSettings.CURATION_STATUS_REJECTED) {
              this.curationStatus = status;
              this.calculateState(false);
              this.postRejectionCurationItem();
            }
          }
        ).then(result => {
        return this.handleTransitionMessages(newStatus, previousStatus, result);
      });
    });
  }

  private handleDiscardCurrentRevision(srcStatus: string, previousStatus, callback?) {
    const data = this.buildCurationData(srcStatus, 'discard current revision');
    this.discardCurationItem(srcStatus, data);
  }

  private computeNewStatus(status: string, previousStatus?: string): Observable<any> {
    if (this.multiStepReview && (status === CurationSettings.CURATION_STATUS_REJECTED || status === CurationSettings.CURATION_STATUS_APPROVED)) {
      return Observable.of(CurationSettings.CURATION_STATUS_REVIEW_FEEDBACK_RECEIVED);
    }

    if (this.isPublishedStatus(status) && !!previousStatus && previousStatus === CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE) {
      return this.showRejectPopUp(status, previousStatus);
    } else {
      return Observable.of(status);
    }

  }

  private showRejectPopUp(status, previousStatus): Observable<any> {
    return this.modalLoader.popupModal(PlaceholderModalComponent, {
      componentToLoad: CurationEditorActionMessageComponent,
      buttons: {
        approveBtn: 'Ok',
        declineBtn: 'Cancel'
      },
      payload: {
        backdropAllowed: false
      },
      approveCallback: (model) => {
        if (!this.curationItem['opinion']) {
          this.curationItem['opinion'] = {};
        }
        this.curationItem['opinion']['rejectMessage'] = model['message'];
        return Observable.of(status);
      },
    }, ModalTypes.REJECT)
      .pipe(map((res) => {
        if (res.payload.status === modalResponse.decline) {
          return previousStatus;
        } else {
          this.createNote(this.curationItem, 'archiving rejected', this.curationItem['opinion']['rejectMessage'] || '', '');
          return status;
        }
      }));
  }

  private showWarningPopup(title: string, content: string): Observable<boolean> {
    return this.modalLoader.popupModal(ConfirmModalComponent,
      {
        title,
        content: this.translateService.instant(content),
        approveBtn: 'OK',
        declineBtn: 'Cancel'
      }, ModalTypes.APPROVE)
      .map(response => response.payload)
      .flatMap((response) => {
          return Observable.of(response === modalResponse.approve);
        }
      );
  }

  private handleTransitionMessages(currentStatus: string, previousStatus: string, callback: any) {
    const immutableData = this.curationItem.curationData['immutableData'];
    const curationDefintion = immutableData ? immutableData.curationDefintion : undefined;
    const ui = curationDefintion ? curationDefintion.ui : undefined;
    const transitionMessages = ui ? ui.transitionMessages : undefined;
    const isReviseFlow = this.revisionCount > 1;
    if (transitionMessages) {
      const messages = transitionMessages.filter(entry => entry.from === previousStatus && entry.to === currentStatus && (entry.reviseOnly === true ? isReviseFlow : true));
      if (messages) {
        messages.forEach(entry => {
          if (entry.requireConfirmation === true) {
            this.modalLoader.popupModal(ConfirmModalComponent,
              {
                title: 'Warning',
                content: entry.message,
                approveBtn: 'OK',
                declineBtn: ''
              }, ModalTypes.ALERT).subscribe(() => {
            });
          } else {
            this.notificationMessageService.info(this.translateService.instant(entry.message));
          }
        });
      }
    }
    return callback;
  }

  private getCurationIdContextArray(dataArray: any[]): string {
    // curationData contains a content array in which curationItemId is located.
    // This is the array item format: reference: "8d727d4c-e78d-11e9-9a5c-005056bc7bdb", resourceType: "CurationItem"
    for (const elem of dataArray) {
      if (elem['resourceType'] === 'CurationItem') {
        return elem['reference'];
      }
    }
    console.error('this.getCurationIdContextArray - CurationItem reference was not found');
    return undefined;
  }

  public reject() {
    let isRejected: boolean = false;
    this.modalLoader.popupModal(PlaceholderModalComponent, {
      componentToLoad: CurationEditorActionMessageComponent,
      buttons: {
        approveBtn: 'Ok',
        declineBtn: 'Cancel'
      },
      payload: {
        backdropAllowed: false
      },
      approveCallback: (model) => {
        isRejected = true;
        if (!this.curationItem['opinion']) {
          this.curationItem['opinion'] = {};
        }
        this.curationItem['opinion']['rejectMessage'] = model['message'];
        return Observable.of(true);
      },
    }, ModalTypes.REJECT)
      .finally(() => {
        if (isRejected) {
          if (!this.multiStepReview) {
            this.createNote(this.curationItem, 'rejected', this.curationItem['opinion']['rejectMessage'] || '', '');
          }
          this.changeStatus(CurationSettings.CURATION_STATUS_REJECTED);
        }
      })
      .subscribe(() => {
      });
  }

  private createNote(curationItem, action: string, message: string, type: string) {
    // Get the text of the approve/reject action (the justification)
    const note = {
      actor: {
        reference: this.actorId,
        resourceType: this.noteActorType
      },
      context: [{
        reference: this.currentRevisionId,
        resourceType: 'WorkflowRequest'
      }],
      parentNote: null,
      text: message ? this.notesService.fixNoteText(message) : '',
      type: type,
      action
    };
    this.coreAC.setLoader(true);
    this.notesService.save(note).then(() => {
    }).catch((err) => {
      console.error(err);
    }).finally(() => {
      this.coreAC.setLoader(false);
    });
  }

  private openApprovalJustificationPopup(approveCallback, finallyCallback) {
    let approved: boolean = false;
    this.modalLoader.popupModal(PlaceholderModalComponent, {
      componentToLoad: CurationEditorActionMessageComponent,
      buttons: {
        approveBtn: 'Ok',
        declineBtn: 'Cancel'
      },
      payload: {
        backdropAllowed: false
      },
      approveCallback: (model) => {
        approved = true;
        approveCallback(model);
        return Observable.of(true);
      },
    }, ModalTypes.APPROVE)
      .finally(() => {
        if (approved) {
          finallyCallback();
        }
      })
      .subscribe(() => {
      });
  }

  revisionHistoryRowClick($event) {
    if (this.routerManagerService?.parameters?.curationId === this.curationId && this.routerManagerService?.parameters?.revisionId === $event.revisionId) {
      this.routerManagerService.reload();
    }
    else {
      const url = 'curation/' + this.curationId + '/' + this.routerManagerService.parameters.workflowRequestId  + '/' + $event.revisionId + '/' + this.routerManagerService.parameters.curationType;
      this.routerManagerService.reload(url)
    }
  }

  copyDataFromRelatedCurations(forceCopy: boolean = false, saveChanges: boolean = true, specificCurationItemId: string = undefined, data?: object): any {
    const copiedItemsRoot = !!data ? data : get(this.conf, 'ui.forms.editorForms.itemsToCopy');
    const promises = [];
    let dataChanged: boolean = false;

    if (!copiedItemsRoot) {
      console.error('CurationEditorAbstract->copyDataFromRelatedCurations: cannot find related entity configuration. Please update: ui.forms.editorForms.itemsToCopy');
      return Promise.reject('copyDataFromRelatedCurations: Missing configuration');
    }

    for (const copyDetails of copiedItemsRoot) {
      promises.push(new Promise((resolve, reject) => {
        const copiedItemDetails = get(this.curationItem.curationData, copyDetails['copiedItemDetailsPathInSource']); // getting copied entity details from the dest item (code, revision etc.)

        if (copiedItemDetails && copiedItemDetails[copyDetails['copiedItemIdField']] &&
          copiedItemDetails[copyDetails['copiedItemRevisionField']]) {
          // Coping items if they were not copied before or in case of forceCopy
          const copiedItemId: string = copiedItemDetails[copyDetails['copiedItemIdField']];

          if ((!get(this.curationItem.curationData, copyDetails['destItem']) || forceCopy) && // Copy only if object was not copied before or in forceCopy mode
            (specificCurationItemId === copiedItemId || specificCurationItemId === undefined)) { // coping only a specific item if specificCurationItemId has value
            // getting the curation item which contains the copied data
            this.curationActionCreatorService.getCurationItemV1(copiedItemId, copiedItemDetails[copyDetails['copiedItemRevisionField']],
              copyDetails['copiedItemType']).subscribe(sourceCuration => {

                if (get(sourceCuration.curationData, copyDetails['sourceItemToCopy'])) {
                  set(this.curationItem.curationData, copyDetails['destItem'], get(sourceCuration.curationData, copyDetails['sourceItemToCopy']).slice());
                  // First time when editor is opened after curation creation, copied items are added to it.
                  // To prevent prompting the user about this when he exists the screen, saving the changes automatically.
                  dataChanged = true;
                  resolve(sourceCuration);
                } else {
                  // In forceCopy mode, if dest item is already exist and the source copied item is empty, deleteing source item (like when regimen with drugs was originally
                  // selected and user selects a different regimen without drugs)
                  if (forceCopy) {
                    if (get(this.curationItem.curationData, copyDetails['destItem'])) {
                      set(this.curationItem.curationData, copyDetails['destItem'], undefined);
                    }
                  }
                  resolve(sourceCuration);
                }
              }
            );
          } else {
            resolve(true);
          }
        } else {
          console.error('CurationEditorAbstract->copyDataFromRelatedCurations: cannot find related entity item: ' + copyDetails['copiedItemDetailsPathInSource']);
          reject();
        }
      }));
    }

    return Promise.all(promises).then(x => {
      if (dataChanged && saveChanges) { // saving only one time after all data was updated
        this.changeStatus(this.curationStatus);
      }
      return x;
    });
  }

  isCandidateForPublishing(status: string): boolean {
    switch (status) {
      case CurationSettings.CURATION_STATUS_WAITING_FOR_APPROVAL:
      case CurationSettings.CURATION_STATUS_INREVIEW:
      case CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED:
        return true;
    }
    return false;
  }

  isBeforeApprovePublish(status: string): boolean {
    switch (status) {
      case CurationSettings.CURATION_STATUS_APPROVED_SUBMITTED:
      case CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED:
        return true;
    }
    return false;
  }


  isPublishedStatus(status: string): boolean {
    return (status.toLowerCase() === CurationSettings.CURATION_STATUS_APPROVED
      || status.toLowerCase() === CurationSettings.CURATION_STATUS_PUBLISHED);
  }

  /*
  Filling curation item revision object for RelatedEntities components
  */
  fillRevisionList(revisionData: any, revisedCurationItemId: any, nameFieldPath: string) {
    const revisionItem: RelatedEntitiesRevision = new RelatedEntitiesRevision();
    if (revisionData) {
      // Filling an array with all approved revisions
      revisionItem['revisedCurationItemId'] = revisedCurationItemId;
      revisionData.forEach(rev => {
        if (this.isPublishedStatus(rev['status'])) {
          const revision = {
            code: rev['revisionId'],
            display: rev['revisionName'],
            curationDataId: rev['id'],
            itemName: rev[nameFieldPath]
          };
          revisionItem.revisions.push(revision);
        } else if (rev['status'] === CurationSettings.CURATION_STATUS_ARCHIVED) {
          revisionItem.revisions.push({code: CurationSettings.CURATION_STATUS_ARCHIVED, display: rev['revisionName']});
        }
      });
    }
    this.revisedEntitiesRevisionsData.push(revisionItem);
  }

  /*
  Returns an array of changed related entities
  */
  checkRevisedRelatedEntities() {
    this.revisedRelatedEntities = [];

    if (this.curationItem.curationData['relatedEntities']) {
      this.curationItem.curationData['relatedEntities'].forEach(entity => {

        if (entity['upToDate'] !== undefined && entity['upToDate'] === false) {
          this.revisedRelatedEntities.push(entity);
        }
      });
    }

    if (this.revisedRelatedEntities.length > 0) {
      this.revisiedEntitiesTableData.rows = [];
      this.updateRevisedRelatedEntitiesTableData();
      this.logRevisedRelatedEntitiesTable(this.curationItem.curationData, this.curationItem['curationItem']['activeRevisionId'],
        this.revisiedEntitiesTableData.rows);
    }
  }

  updateRevisedRelatedEntitiesTableData() {
  }

  logRevisedRelatedEntitiesTable(curationData: any, revisionId: string, revisedEntitiesTableData: any) {
    if (curationData) {
      const logMessage = 'RevisionId: ' + revisionId +
        ', CurationDataId: ' + curationData.id +
        ', by: ' + curationData.performerName +
        ', of type:' + curationData.content.type;

      console.log(logMessage + ', Items for review: ', JSON.parse(JSON.stringify(revisedEntitiesTableData)),
        ', relatedEntities: ', JSON.parse(JSON.stringify(curationData.relatedEntities)));
    }
  }

  /*
  Requesting the server for all related entities revisions of the current curation.
  A certain curation may have multiple related entities.
  */
  getAllRelatedEntitiesRevisions(): Promise<void> {
    this.revisedEntitiesRevisionsData = [];
    if (!this.revisedRelatedEntities || this.revisedRelatedEntities.length === 0) {
      return Promise.resolve(); // no related entity was revised
    }
    if (!this.confMap) {
      this.curationActionCreatorService.getState(['definitions'])
        .take(1).subscribe((data => {
        this.confMap = Array.from(data.values()).filter(data => data['type'] === 'curationKB');
      }));
    }
    const promises = [];
    this.revisedRelatedEntities.forEach(entity => {
      promises.push(new Promise((resolve, reject) => {
          if (entity['revisedCurationItemId']) {
            const relatedEntityDataExists = this.revisedEntitiesRevisionsData.find(item => item['revisedCurationItemId'] === entity['revisedCurationItemId']);
            if (!relatedEntityDataExists) {
              let relatedEntityNameFieldPath: string; // Holds the path of the related entity name field in curation data (e.g. content.treatmentPlanName)
              const curationKbDefinition: any = Array.from(this.confMap).find(data => data['type'] === 'curationKB');
              if (!!curationKbDefinition) {
                // Test path value by curationItemType
                if (!!curationKbDefinition.ui.relatedEntitiesCreation.entityNamePathByType
                  && Array.isArray(curationKbDefinition.ui.relatedEntitiesCreation.entityNamePathByType) && curationKbDefinition.ui.relatedEntitiesCreation.entityNamePathByType.length > 0) {
                  const descr = curationKbDefinition.ui.relatedEntitiesCreation.entityNamePathByType.find(item => !!item.alias ? item.alias === entity.curationItemType : item.type === entity.curationItemType);
                  relatedEntityNameFieldPath = !!descr ? descr.path : undefined;
                }

                if (!relatedEntityNameFieldPath && curationKbDefinition.ui.relatedEntitiesCreation.entityNamePath && curationKbDefinition.ui.relatedEntitiesCreation.entityNamePath[entity.curationItemType]) {
                  relatedEntityNameFieldPath = curationKbDefinition.ui.relatedEntitiesCreation.entityNamePath[entity.curationItemType];
                }
              }
              const fieldsList: string = relatedEntityNameFieldPath ? 'revisionId,revisionName,status,' + relatedEntityNameFieldPath : 'revisionId,revisionName,status';
              // In case same related entity type exists multiple times in a certain component, retrieving its data only once
              this.curationActionCreatorService.getCurationItemRevisions(entity['revisedCurationItemId'], {
                '_fieldslist': fieldsList,
                '_sort:desc': 'timestamp'
              })
                .subscribe(data => {
                  // last revision should be last - sorting by date
                  data = data.sort((a, b) => {
                    return (new Date(a.meta.created).getTime() - new Date(b.meta.created).getTime());
                  });
                  this.fillRevisionList(data, entity['revisedCurationItemId'], relatedEntityNameFieldPath);
                  return resolve(true);
                });
            } else {
              return resolve(true);
            }
          } else {
            console.error(`getAllRelatedEntitiesRevisions->getAllRelatedEntitiesRevisions: revisedCurationItemId was not saved for the current related entity. Please update 'relatedEntitiesCreation' configuraton`);
            return reject();
          }
        })
      );
    });

    return Promise.all(promises).then(() => {
      return Promise.resolve();
    });
  }

  prepareDataToSave(status: string, prevStatus: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  downloadApprovalPDF($event) {
    if (this.curationItem.curationData['content']['approvalDocument'] && this.curationItem.curationData['content']['approvalDocument']['referenceId']) {
      const referenceId = this.curationItem.curationData['content']['approvalDocument']['referenceId'];

      this.curationActionCreatorService.getDownloadedFile(referenceId).subscribe(
        data => {
          const file = {data: data.base64Data};
          const fileExtension: string = this.curationItem.curationData['content']['approvalDocument'].name.split('.').pop();
          this.pdfService.downloadPDF(file, CurationSettings.APPROVAL_DOC_NAME + fileExtension, referenceId);
        }
      );
    } else {
      console.error('CurationEditorRegimenComponent->downloadApprovalPDF Error: cannot find approvalDocument: referenceId');
    }
  }

  getCurrentRelatedEntitiesIds(): Array<string> {
    return null;
  }

  protected showNotesPanel(title?: string, nodeId?: string, newNoteCallback?: (...args) => void) {
    this.currentNotesPanelTitle = title;
    const revisionId = this.currentRevisionId;
    const contextType = nodeId ? ['NodeRevision', 'NodeId'] : 'WorkflowRequest';
    const contextId = nodeId ? [revisionId, nodeId] : revisionId;
    const readOnly = this.isPublishedStatus(this.curationStatus) || !(this.curatorUserMode || this.isReviewer);
    const definition = PanelLoaderPayload.builder()
      .setTitle(title || 'Notes')
      .setPayload({
        contextType,
        contextId,
        actorType: this.noteActorType,
        newNotes: false,
        actorId: this.actorId,
        filters: false,
        classes: 'note-input-padding',
        readOnly
      })
      .setApproveCallback((note: any) => {
        if (newNoteCallback) {
          newNoteCallback(note);
        }
      });
    this.notesPanelClass = 'limited-height-notes-panel' + (readOnly ? ' read-only' : '');
    this.panelService.showLayout(NotesModalWrapperComponent, definition, this.notesPanelId);
  }

  protected hideNotesPanel() {
    this.panelService.hideLayout(this.notesPanelId);
  }

  private checkUnreadNotes() {
    this.getCurationItemNotes().toPromise().then((res) => {
      if (res && res.status === 200 && res.body) {
        this.newNotes = res.body.filter((note) => !note.read).length;
      } else {
        this.newNotes = 0;
      }
    });
  }

  public toggleNotesPanel(title?: string) {
    if (!this.isNotesPanelOpen || title !== this.currentNotesPanelTitle) {
      this.showNotesPanel(title);
    } else {
      this.hideNotesPanel();
    }
  }

  public toggleDisplayStatusChanged(display: boolean) {
    this.isNotesPanelOpen = display;
  }

  // ---------- common methods --------------
  public getListTitle() {
    return !!this.editorFormsMap ? get(this.editorFormsMap, LIST_TITLE, DRAGS_LIST) : DRAGS_LIST;
  }

  public setListDataPath() {
    this.listDataPath = 'content.' + get(this.editorFormsMap, 'listData', 'drugs');
  }

  public getListData() {
    return get(this.curationItem.curationData, this.listDataPath, undefined);
  }

  public setListData(data) {
    set(this.curationItem.curationData, this.listDataPath, data);
  }

  public getItemTitle() {
    return !!this.editorFormsMap ? get(this.editorFormsMap, ITEM_TITLE, DRAG) : DRAG;
  }

  public getFormName() {
    if (!!this.editorFormsMap) {
      const formDescr = get(this.editorFormsMap, 'listData', 'drugs');
      return this.editorFormsMap[formDescr]['name'];
    }
    return this.editorFormsMap['drugs']['name'];
  }

  public getDataFormDescriptor() {
    if (!!this.editorFormsMap) {
      const formDescr = get(this.editorFormsMap, 'listData', 'drugs');
      return this.editorFormsMap[formDescr];
    }
    return this.editorFormsMap['drugs'];
  }

  public getItemAllowDuplicate() {
    const formsDescr = this.getDataFormDescriptor();
    return !!formsDescr ? get(formsDescr, 'allowDuplicate', true) : true;
  }

  public hasForm(name): boolean {
    return has(this.editorFormsMap, name);
  }

  public cleanData(dataList: any[], element: string): any[] {
    // remove empty elements
    if (!!dataList && dataList.length > 0) {
      const newDataList = dataList.filter(item =>
        (!!get(item, element, undefined) || !!item.__newItem__)
        && !(get(item, element, undefined) === undefined && item.__newItem__)
      );
      // Delete 'new item' flag before saving - this fixes GEN-GEN-35629.
      newDataList.forEach(item => unset(item, CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME));
      return newDataList;
    }
    return dataList;
  }


  protected getDbName(): string {
    const grantedKb = this.curationActionCreatorService.getDb();
    if (!!grantedKb) {
      return grantedKb;
    }

    const contextDescr = this.conf['context'].filter(item => {
      return item.resourceType === 'kb';
    });
    if (contextDescr.length > 0) {
      return contextDescr[0]['reference'];
    }
    return null;
  }

  private defineShowPreviewButton(descr: any) {
    const requiredPermission = !!descr ? get(descr, 'requiredPermission', undefined) : undefined;
    return !!requiredPermission && this.authService.doesUserHasPermission(requiredPermission);
  }

  protected getRejectMessage(status) {
    let rejectMessage;
    if (status === CurationSettings.CURATION_STATUS_REJECTED) {
      rejectMessage = this.curationItem['opinion']['rejectMessage'];
    }
    return rejectMessage;
  }

  protected isEditableStatus(status): boolean {
    return (status === CurationSettings.CURATION_STATUS_DRAFT || status === CurationSettings.CURATION_STATUS_REVIEW_FEEDBACK_RECEIVED);
  }

  public reassignCurationItem() {
    switch (this.curationStatus) {
      case CurationSettings.CURATION_STATUS_DRAFT:
      case CurationSettings.CURATION_STATUS_INREVIEW:
      case CurationSettings.CURATION_STATUS_REVIEW_FEEDBACK_RECEIVED:
      case CurationSettings.CURATION_STATUS_WAITING_FOR_ARCHIVE:
        this.setReassignButtonDisabled(true);

        const tempAnalysis = {
          activeRevision: this.routerManagerService.parameters?.revisionId,
          workflowrequest_id: this.routerManagerService.parameters?.workflowRequestId,
          curationItemID: this.curationId,
          type: this.curationType,
          kb: this.getDbName(),
          status: this.curationStatus
        };
        let allowedRolesForReassignment: any;
        this.userActionCreatorService.getStoredOrganizationConfiguration()
          .take(1)
          .map((organization) => {
            allowedRolesForReassignment = organization.allowedRolesForReassignmentCurationItem ? organization.allowedRolesForReassignmentCurationItem : null;
          }).toPromise();

        const modalPayload: any = {};
        modalPayload.name = this.curationKey;
        modalPayload.testNumber = '';
        modalPayload.analysis = tempAnalysis;
        modalPayload.practitionerRoles = allowedRolesForReassignment;
        modalPayload.enableReassign = allowedRolesForReassignment !== null ?
          !!(allowedRolesForReassignment.find(role => role.toLocaleLowerCase() === this.authService.getUser().role.toLocaleLowerCase())) : true;
        modalPayload.practitionerFilterBy = modalPayload.analysis.practitionerFilterBy;
        modalPayload.disallowedPractitionerRoles = ['Provider'];
        modalPayload.modalName = 'ReassignCuration';
        modalPayload.title = modalPayload.name;

        this.unsavedChangesExist(true).pipe(
          take(1)
        ).subscribe(
          (unsavedChangesExist => {
            if (!unsavedChangesExist) {
              return this.openReassignModalView(modalPayload);
            } else {
              return this.openDiscardChangesBeforeReassignModalView(modalPayload);
            }
          })
        );

        break;
    }
  }

  private async openReassignModalView(modalPayload) {
    this.modalLoader.popupModal(ReassignTestModalComponent,
      {
        content: modalPayload,
        backdropAllowed: false
      }, ModalTypes.NONE).subscribe((data) => {
      if (data && data.payload) {
        this.setReassignButtonDisabled(false);
        if (data.payload.action === 'close') {
          Observable.forkJoin([this.workflowConfigService.changeWorkflow(this.routerManagerService.parameters?.workflowRequestId, this.currentRevisionId)])
            .subscribe(() => {
              this.curationActionCreatorService.getAssignedPractioners().pipe(
                retry(2),
                map(practIds => {
                  if (practIds === null || isEmpty(practIds)) {
                    throwError('Failed getting assigned practitioner Id');
                  }
                  this.assignedPractitionerId = (!isEmpty(practIds)) ? practIds[0] : null;
                  this.getDataAndConfiguration(this.routerManagerService.parameters?.curationId, true);
                })
              ).subscribe();
            });
        }
      }
    });
  }

  private async openDiscardChangesBeforeReassignModalView(modalPayload) {
    const cont: string = 'You have unsaved changes.\n\rIf you reassign the item without saving your changes, all changes will be lost.';
    this.modalSubscriber$ = this.modalLoader.popupModal(ConfirmModalComponent,
      {
        title: 'Unsaved changes',
        content: cont,
        declineBtn: 'Cancel reassign',
        approveBtn: 'Continue reassign'
      })
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe((response: modalResponse) => {
          switch (response) {
            case modalResponse.approve:
              setTimeout(() => this.openReassignModalView(modalPayload), 10);
              break;
            case modalResponse.decline:
            default:
              this.setReassignButtonDisabled(false);
              return false;
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        },
        () => {
        },
        () => {
          this.modalSubscriber$ = null;
        });
  }

  private setReassignButtonDisabled(isDisabled: boolean) {
    this.isReassignButtonDisabled = isDisabled;
    this.detector.markForCheck();
  }

  public submitCurationItemForReview() {
    this.formHandler.popupDynamicFormModal(
      'Submit For Review',
      'SUBMIT-FOR-REVIEW',
      {},
      'Submit',
      'Cancel',
      null,
    ).subscribe(data => {
      this.populateSubmitCurationItemForReview(data);
    });
  }

  private populateSubmitCurationItemForReview(submittedData) {
    if (get(submittedData, 'payload.formData.reviewer.id', null) !== null) {
      this.submitCurationItemForReviewReq(submittedData.payload.formData.reviewer.id, submittedData.payload.formData.notes);
    }
  }

  private submitCurationItemForReviewReq(reviewer, note, status: string = CurationSettings.CURATION_STATUS_INREVIEW) {
    const workflowRequestId = this.curationItem['curationItem']['workflowRequestId'];
    const type = this.curationItem['curationItem']['type'];
    const this$ = this;

    this.changeStatus(status, () => {
      this$.curationActionCreatorService.submitCurationItemForReview(this$.curationItem['curationItem']['id'],
        this$.currentRevisionId, workflowRequestId, this$.actorId, reviewer, type, status)
        .subscribe(() => {
          if (note) {
            return this$.diagnosticOrderService.createTestAssignNote(workflowRequestId, this$.currentRevisionId, note)
              .subscribe(
                (note) => {
                  if (note) {
                    this.activityService.create(
                      {
                        actor: {
                          reference: note.body.actor.id,
                          resourceType: note.body.actor.resourceType
                        },
                        context: [{
                          reference: note.body.id,
                          resourceType: 'Note'
                        }],
                        action: ActivityActions.READ
                      })
                      .subscribe(() => {
                        return true;
                      });
                  }
                },
                () => {
                  this.notificationMessageService.error('error while adding note');
                }
              );
          }
        });
    });
  }

  public viewArchiveReason() {
    const title = get(this.showArchiveReasonConf, 'title', 'Archive Reason'); // + ' ' + this.curationKey;
    const formName = get(this.showArchiveReasonConf, 'formId', '');
    const archiveButtonText = get(this.showArchiveReasonConf, 'archiveButtonText', 'Archive');

    this.formHandler.popupDynamicFormModal(title, formName, {}, archiveButtonText, 'Cancel')
      .subscribe(data => {
          if (get(data, 'status') === 1 && has(data, 'payload.formData')) {
            // execute here what we need
            this.saveArchiveNote(get(data, 'payload.formData', {}));
            // save data   and execute archive
            this.changeStatus(CurationSettings.CURATION_STATUS_ARCHIVED_SUBMITTED).then(() => {
            });
          }
        }
      );
  }

  // create new note of 'archived' type and store there reason and description
  private saveArchiveNote(data: any) {
    this.createNote(this.curationItem, 'archived', '<b>Reason:</b> ' + data.reasonForArchive.display +
      (!!data.reasonForArchiveDescription && trim(data.reasonForArchiveDescription).length > 0 ? '<br><b>Description:</b> ' + data.reasonForArchiveDescription : ''), 'archiveReason');
  }

  public preFetchForms(formIds: Array<string>) {
    if (formIds) {
      formIds.forEach(formId => this.formHandler.getForm(formId));
    }
  }

  protected createTreeViewMenuItems(treeViewConfig: any): MenuItem[] {
    return [];
  }
}
